import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    Toolbar,
    SaveButton,
    required,
    regex,
    useRecordContext,
    useRedirect,
    useTranslate,
} from 'react-admin';
import { Box, Card, CardContent, Typography, Chip, IconButton } from '@mui/material';
import { SystemUpdate, PhoneIphone, Android, ArrowBack } from '@mui/icons-material';

const SEMVER_REGEX = /^\d+(\.\d+){0,2}(-[a-zA-Z0-9.]+)?$/;
const URL_REGEX = /^https?:\/\/.+/;

const validateVersionsOrder = (values: any) => {
    const errors: any = {};
    if (values?.minimumVersion && values?.latestVersion) {
        const pad = (v: string): number[] =>
            v.split('-')[0].split('.').map((n) => parseInt(n, 10) || 0);
        const min = pad(values.minimumVersion);
        const lat = pad(values.latestVersion);
        for (let i = 0; i < Math.max(min.length, lat.length); i++) {
            const m = min[i] ?? 0;
            const l = lat[i] ?? 0;
            if (m > l) {
                errors.minimumVersion = 'resources.app-versions.errors.minimumExceedsLatest';
                break;
            }
            if (m < l) break;
        }
    }
    return errors;
};

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: 4,
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton />
    </Toolbar>
);

const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    if (!record) return null;
    const isIos = record.platform === 'ios';
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/app-versions')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back', { _: 'Back' })}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isIos
                        ? <PhoneIphone sx={{ fontSize: { xs: 24, sm: 28 }, color: '#1976d2' }} />
                        : <Android sx={{ fontSize: { xs: 24, sm: 28 }, color: '#00C853' }} />
                    }
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                        {isIos ? 'iOS' : 'Android'} — {translate('resources.app-versions.name')}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '1rem' }, mt: 0.5 }}>
                    {record.appIdentifier}
                </Typography>
            </Box>
            <Chip
                label={record.enabled ? 'Enabled' : 'Disabled'}
                color={record.enabled ? 'success' : 'default'}
                size="small"
            />
        </Box>
    );
};

export const AppVersionEdit = () => {
    const translate = useTranslate();
    return (
        <Edit
            title={false}
            component="div"
            mutationMode="pessimistic"
            redirect="list"
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                validate={validateVersionsOrder}
                sanitizeEmptyValues
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Version Configuration */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <SystemUpdate sx={{ mr: 2, color: '#1976d2' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.app-versions.sections.versions', { _: 'Versions' })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectInput
                                    source="platform"
                                    choices={[
                                        { id: 'ios', name: 'iOS' },
                                        { id: 'android', name: 'Android' },
                                    ]}
                                    disabled
                                    fullWidth
                                />
                                <TextInput
                                    source="latestVersion"
                                    validate={[required(), regex(SEMVER_REGEX, 'resources.app-versions.errors.invalidSemver')]}
                                    helperText="resources.app-versions.helpers.latestVersion"
                                    fullWidth
                                />
                                <TextInput
                                    source="minimumVersion"
                                    validate={[required(), regex(SEMVER_REGEX, 'resources.app-versions.errors.invalidSemver')]}
                                    helperText="resources.app-versions.helpers.minimumVersion"
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Store Configuration */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <PhoneIphone sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.app-versions.sections.store', { _: 'Store Configuration' })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="appIdentifier"
                                    validate={[required()]}
                                    helperText="resources.app-versions.helpers.appIdentifier"
                                    fullWidth
                                />
                                <TextInput
                                    source="storeUrl"
                                    validate={[required(), regex(URL_REGEX, 'resources.app-versions.errors.invalidUrl')]}
                                    helperText="resources.app-versions.helpers.storeUrl"
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Update Prompt */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <SystemUpdate sx={{ mr: 2, color: '#F57C00' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.app-versions.sections.prompt', { _: 'Update Prompt' })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="releaseNotes"
                                    multiline
                                    rows={4}
                                    helperText="resources.app-versions.helpers.releaseNotes"
                                    fullWidth
                                />
                                <BooleanInput
                                    source="enabled"
                                    helperText="resources.app-versions.helpers.enabled"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};
